import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useIntro } from "../../context/IntroContext";
import useLocalTime from "../../hooks/useLocalTime";
import { profile } from "../../data/resume";
import "./loader.css";

const MIN_DURATION = 1800;
const MAX_DURATION = 5000;
const COLUMNS = 6;
const RING = 2 * Math.PI * 92;
const lift = [0.76, 0, 0.24, 1];

const logLines = [
  { at: 0, text: "resolving modules" },
  { at: 24, text: "compiling interface" },
  { at: 52, text: "hydrating components" },
  { at: 78, text: "warming caches" },
  { at: 100, text: "ready — welcome", done: true }
];

const pageLoaded = () =>
  new Promise((resolve) => {
    if (document.readyState === "complete") resolve();
    else window.addEventListener("load", resolve, { once: true });
  });

// Intro shown on every full page load: monogram draws, progress tracks real
// loading (window load + fonts), then the screen lifts away in columns.
const Loader = () => {
  const { setReady } = useIntro();
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("loading"); // loading → exit → done
  const loaded = useRef(false);
  const time = useLocalTime(profile.timeZone);
  const reduceMotion = useRef(window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  useEffect(() => {
    document.documentElement.classList.add("is-loading");
    Promise.all([pageLoaded(), document.fonts?.ready]).then(() => {
      loaded.current = true;
    });

    const start = performance.now();
    const minimum = reduceMotion.current ? 600 : MIN_DURATION;
    let frame;
    let value = 0;

    const tick = (now) => {
      const elapsed = now - start;
      const finished = (loaded.current && elapsed >= minimum) || elapsed >= MAX_DURATION;
      // Creep toward 90% on a time curve until loading actually finishes, then sprint to 100.
      const target = finished ? 100 : Math.min(90, (elapsed / minimum) * 90);
      value += (target - value) * (finished ? 0.22 : 0.1);
      if (finished && value > 99.5) value = 100;
      setProgress(value);

      if (value < 100) {
        frame = requestAnimationFrame(tick);
      } else {
        setTimeout(() => setPhase("exit"), reduceMotion.current ? 0 : 300);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (phase !== "exit") return undefined;
    // Start the page's entrance animations as the columns begin to lift.
    const id = setTimeout(() => setReady(true), reduceMotion.current ? 0 : 450);
    return () => clearTimeout(id);
  }, [phase, setReady]);

  const finish = () => {
    document.documentElement.classList.remove("is-loading");
    setPhase("done");
  };

  if (phase === "done") return null;

  const pct = Math.round(progress);
  const exiting = phase === "exit";
  const visibleLines = logLines.filter((line) => progress >= line.at);
  const activeLine = visibleLines[visibleLines.length - 1];

  return (
    <div className={`loader ${exiting ? "is-exiting" : ""}`} role="status" aria-live="polite" aria-label={`Loading ${pct}%`}>
      <div className="loader__columns" aria-hidden="true">
        {Array.from({ length: COLUMNS }, (_, i) => (
          <motion.span
            key={i}
            className="loader__column"
            initial={false}
            animate={exiting ? { y: "-101%" } : { y: "0%" }}
            transition={
              reduceMotion.current
                ? { duration: 0.3 }
                : { duration: 0.9, ease: lift, delay: 0.35 + Math.abs(i - (COLUMNS - 1) / 2) * 0.07 }
            }
            onAnimationComplete={i === 0 && exiting ? finish : undefined}
          />
        ))}
      </div>

      <AnimatePresence>
        {!exiting && (
          <motion.div
            className="loader__content"
            exit={{ opacity: 0, scale: 0.96, filter: "blur(6px)" }}
            transition={{ duration: 0.4, ease: lift }}
            aria-hidden="true"
          >
            <div className="loader__corner loader__corner--tl mono">
              <span>Yaseen Al-Midani</span>
              <span className="loader__dim">Portfolio © {new Date().getFullYear()}</span>
            </div>
            <div className="loader__corner loader__corner--tr mono">
              <span>{profile.city}</span>
              <span className="loader__dim">{time} GST</span>
            </div>

            <div className="loader__center">
              <div className="loader__mark">
                <svg viewBox="0 0 200 200" className="loader__ring">
                  <circle cx="100" cy="100" r="92" className="loader__ring-track" />
                  <circle
                    cx="100"
                    cy="100"
                    r="92"
                    className="loader__ring-fill"
                    style={{ strokeDasharray: RING, strokeDashoffset: RING * (1 - progress / 100) }}
                  />
                </svg>
                <svg viewBox="0 0 64 64" className="loader__logo">
                  <rect x="4" y="4" width="56" height="56" rx="15" pathLength="1" className="loader__stroke loader__stroke--frame" />
                  <path d="M17 18l9 14v14" pathLength="1" className="loader__stroke loader__stroke--y1" />
                  <path d="M35 18l-9 14" pathLength="1" className="loader__stroke loader__stroke--y2" />
                  <path d="M36 46l7-28 7 28" pathLength="1" className="loader__stroke loader__stroke--a1" />
                  <path d="M39 37h8" pathLength="1" className="loader__stroke loader__stroke--a2" />
                </svg>
                <span className="loader__scan" />
              </div>

              <p className="loader__name">
                {profile.name.split("").map((char, i) => (
                  <span key={i} style={{ animationDelay: `${0.55 + i * 0.035}s` }}>
                    {char === " " ? " " : char}
                  </span>
                ))}
              </p>
              <p className="loader__role mono">{profile.role}</p>
            </div>

            <ul className="loader__log mono">
              {visibleLines.map((line) => (
                <li key={line.text} className={line.done ? "is-done" : ""}>
                  <span className="loader__log-prefix">{line.done ? "✓" : "›"}</span> {line.text}
                  {line === activeLine && !line.done && <span className="loader__log-dots" />}
                </li>
              ))}
            </ul>

            <div className="loader__bar">
              <span style={{ transform: `scaleX(${progress / 100})` }} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Loader;
